import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { X, User, Phone, Mail, MapPin, Calendar, Package, Camera, CheckCircle2, PhoneCall, AlertCircle, Image as ImageIcon, FileText, Boxes, Trash2, Video as VideoIcon, MessageCircle } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
// =====================================================
// EMAILJS CONFIGURATION
// Replace these placeholder values with your real EmailJS credentials
// in BOTH BookingForm.tsx and QuoteModal.tsx.
// =====================================================
const EMAILJS_SERVICE_ID = 'YOUR_SERVICE_ID';
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID';
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY';
// File limits — generous, with fallback messaging if too big to attach
const MAX_FILES = 15;
const MAX_TOTAL_BYTES = 50 * 1024 * 1024; // 50MB total
const MAX_ATTACH_BYTES = 1.8 * 1024 * 1024; // EmailJS practical attach limit
type FormState = 'idle' | 'sending' | 'success' | 'error';
interface SelectedFile {
  file: File;
  previewUrl: string;
  isVideo: boolean;
}
export function QuoteModal({
  open,
  onClose,
  packageTitle
}: {
  open: boolean;
  onClose: () => void;
  packageTitle: string;
}) {
  const {
    lang
  } = useLanguage();
  const text = t[lang].quoteModal;
  const formRef = useRef<HTMLFormElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [files, setFiles] = useState<SelectedFile[]>([]);
  const [specialItems, setSpecialItems] = useState<string[]>([]);
  const totalBytes = files.reduce((s, f) => s + f.file.size, 0);
  const totalMB = (totalBytes / (1024 * 1024)).toFixed(1);
  // Lock body scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  // Reset state on close
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setFormState('idle');
        setFiles(prev => {
          prev.forEach(f => URL.revokeObjectURL(f.previewUrl));
          return [];
        });
        setSpecialItems([]);
        setErrorMsg('');
        formRef.current?.reset();
      }, 300);
    }
  }, [open]);
  // Scroll modal to top when state changes (error banner / success view)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [formState, errorMsg]);
  const addFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const incoming = Array.from(newFiles);
    const combined = [...files];
    let totalSize = totalBytes;
    for (const f of incoming) {
      if (combined.length >= MAX_FILES) break;
      if (totalSize + f.size > MAX_TOTAL_BYTES) {
        setErrorMsg(text.photosTooLarge);
        break;
      }
      combined.push({
        file: f,
        previewUrl: URL.createObjectURL(f),
        isVideo: f.type.startsWith('video/')
      });
      totalSize += f.size;
    }
    setFiles(combined);
  };
  const removeFile = (index: number) => {
    setFiles(prev => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[index].previewUrl);
      copy.splice(index, 1);
      return copy;
    });
    setErrorMsg('');
  };
  const toggleSpecialItem = (item: string) => {
    setSpecialItems(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };
  const isPlaceholderCreds = EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID' || EMAILJS_TEMPLATE_ID === 'YOUR_TEMPLATE_ID' || EMAILJS_PUBLIC_KEY === 'YOUR_PUBLIC_KEY';
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;
    setFormState('sending');
    setErrorMsg('');
    // Demo / preview fallback: simulate a successful send when creds aren't configured
    if (isPlaceholderCreds) {
      await new Promise(r => setTimeout(r, 1200));
      setFormState('success');
      return;
    }
    try {
      // If total file size is too big to attach, strip files from the form
      // and instead send a metadata summary so the email at least gets through.
      if (totalBytes > MAX_ATTACH_BYTES) {
        const photoInput = formRef.current.querySelector('input[name="photos"]') as HTMLInputElement | null;
        if (photoInput) photoInput.value = '';
        const summary = files.map(f => `${f.file.name} (${(f.file.size / (1024 * 1024)).toFixed(1)} MB${f.isVideo ? ', video' : ''})`).join('\n');
        let summaryField = formRef.current.querySelector('input[name="files_summary"]') as HTMLInputElement | null;
        if (!summaryField) {
          summaryField = document.createElement('input');
          summaryField.type = 'hidden';
          summaryField.name = 'files_summary';
          formRef.current.appendChild(summaryField);
        }
        summaryField.value = `${files.length} file(s), ${totalMB} MB total — too large to attach. Customer will send via WhatsApp/SMS.\n${summary}`;
      }
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, {
        publicKey: EMAILJS_PUBLIC_KEY
      });
      setFormState('success');
    } catch (err) {
      console.error('EmailJS error:', err);
      setFormState('error');
      setErrorMsg(text.error);
    }
  };
  const filesTooLargeToAttach = totalBytes > MAX_ATTACH_BYTES;
  return <AnimatePresence data-id="element-396">
      {open && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-397">
          <motion.div className="absolute inset-0 bg-black/80 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={formState !== 'sending' ? onClose : undefined} data-id="element-398" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 30
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 22
      }} className="relative w-full max-w-3xl max-h-[95vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col" data-id="element-399">
            {formState === 'success' ? <SuccessView text={text} onClose={onClose} hadLargeFiles={filesTooLargeToAttach && !isPlaceholderCreds} data-id="element-400" /> : <>
                {/* Header */}
                <div className="bg-mf-black text-white px-6 md:px-10 py-6 relative flex-shrink-0" data-id="element-401">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[100px] bg-mf-red/20 blur-[80px] rounded-full pointer-events-none" data-id="element-402"></div>
                  <button onClick={onClose} disabled={formState === 'sending'} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10 disabled:opacity-40" aria-label="Close" data-id="element-403">
                    <X size={20} data-id="element-404" />
                  </button>
                  <div className="relative z-10" data-id="element-405">
                    <div className="inline-flex items-center bg-mf-red/20 border border-mf-red/40 rounded-full px-3 py-1 mb-3" data-id="element-406">
                      <Package size={14} className="text-mf-yellow mr-2" data-id="element-407" />
                      <span className="text-xs font-bold uppercase tracking-wider text-mf-yellow" data-id="element-408">
                        {text.selectedPackage}: {packageTitle}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-4xl font-condensed font-black uppercase tracking-tight" data-id="element-409">
                      {text.title}
                    </h2>
                    <p className="text-gray-300 text-sm mt-2 max-w-2xl" data-id="element-410">
                      {text.subtitle}
                    </p>
                  </div>
                </div>

                {/* Error banner pinned at top of body */}
                {(formState === 'error' || errorMsg) && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-red-50 border-b border-red-200 px-6 md:px-10 py-3 flex items-start flex-shrink-0" data-id="element-411">
                    <AlertCircle className="text-mf-red mr-3 flex-shrink-0 mt-0.5" size={20} data-id="element-412" />
                    <p className="text-mf-red text-sm font-medium" data-id="element-413">
                      {errorMsg || text.error}
                    </p>
                  </motion.div>}

                {/* Body */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto" data-id="element-414">
                  <form ref={formRef} onSubmit={handleSubmit} className="p-6 md:p-10 space-y-10" data-id="element-415">
                    <input type="hidden" name="package" value={packageTitle} data-id="element-416" />
                    <input type="hidden" name="special_items" value={specialItems.join(', ')} data-id="element-417" />

                    <Section icon={<User size={18} data-id="element-419" />} title={text.sectionContact} data-id="element-418">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-420">
                        <Field label={text.nameLabel} data-id="element-421">
                          <Input name="name" required icon={<User size={16} data-id="element-423" />} data-id="element-422" />
                        </Field>
                        <Field label={text.phoneLabel} data-id="element-424">
                          <Input name="phone" type="tel" required placeholder="(813) 476-8935" icon={<Phone size={16} data-id="element-426" />} data-id="element-425" />
                        </Field>
                        <Field label={text.emailLabel} fullWidth data-id="element-427">
                          <Input name="email" type="email" required icon={<Mail size={16} data-id="element-429" />} data-id="element-428" />
                        </Field>
                      </div>
                    </Section>

                    <Section icon={<MapPin size={18} data-id="element-431" />} title={text.sectionAddresses} data-id="element-430">
                      <div className="space-y-5" data-id="element-432">
                        <Field label={text.originLabel} data-id="element-433">
                          <Input name="origin" required placeholder={text.originPlaceholder} icon={<MapPin size={16} data-id="element-435" />} data-id="element-434" />
                        </Field>
                        <Field label={text.destLabel} data-id="element-436">
                          <Input name="destination" required placeholder={text.destPlaceholder} icon={<MapPin size={16} className="text-mf-red" data-id="element-438" />} data-id="element-437" />
                        </Field>
                        <Field label={text.stairsLabel} data-id="element-439">
                          <Select name="stairs" options={text.stairsOptions} required data-id="element-440" />
                        </Field>
                      </div>
                    </Section>

                    <Section icon={<Calendar size={18} data-id="element-442" />} title={text.sectionDate} data-id="element-441">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-443">
                        <Field label={text.dateLabel} data-id="element-444">
                          <Input name="date" type="date" required icon={<Calendar size={16} data-id="element-446" />} data-id="element-445" />
                        </Field>
                        <Field label={text.dateFlexLabel} data-id="element-447">
                          <Select name="date_flexibility" options={text.dateFlexOptions} required data-id="element-448" />
                        </Field>
                      </div>
                    </Section>

                    <Section icon={<Boxes size={18} data-id="element-450" />} title={text.sectionInventory} data-id="element-449">
                      <div className="space-y-5" data-id="element-451">
                        <Field label={text.inventoryLabel} data-id="element-452">
                          <Textarea name="inventory" required rows={4} placeholder={text.inventoryPlaceholder} data-id="element-453" />
                        </Field>

                        <div data-id="element-454">
                          <label className="block text-sm font-bold text-mf-darkgray mb-3 uppercase tracking-wide" data-id="element-455">
                            {text.specialItemsLabel}
                          </label>
                          <div className="flex flex-wrap gap-2" data-id="element-456">
                            {text.specialItems.map(item => {
                        const active = specialItems.includes(item);
                        return <button type="button" key={item} onClick={() => toggleSpecialItem(item)} className={`px-3 py-2 rounded-full border-2 text-sm font-medium transition-all ${active ? 'bg-mf-red border-mf-red text-white shadow-md' : 'bg-white border-gray-200 text-gray-700 hover:border-mf-red hover:text-mf-red'}`} data-id="element-457">
                                  {item}
                                </button>;
                      })}
                          </div>
                        </div>

                        <Field label={text.packingLabel} data-id="element-458">
                          <Select name="packing" options={text.packingOptions} required data-id="element-459" />
                        </Field>

                        <Field label={text.notesLabel} data-id="element-460">
                          <Textarea name="notes" rows={3} placeholder={text.notesPlaceholder} data-id="element-461" />
                        </Field>
                      </div>
                    </Section>

                    {/* Photos & Videos */}
                    <Section icon={<Camera size={18} data-id="element-463" />} title={text.sectionPhotos} data-id="element-462">
                      <label className="block" data-id="element-464">
                        <div className="border-2 border-dashed border-gray-300 hover:border-mf-red rounded-2xl p-8 text-center cursor-pointer transition-colors bg-gray-50 hover:bg-red-50/30" data-id="element-465">
                          <ImageIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-466" />
                          <div className="font-bold text-mf-black mb-1" data-id="element-467">
                            {text.photosLabel}
                          </div>
                          <div className="text-xs text-gray-500 mb-4" data-id="element-468">
                            {text.photosHint}
                          </div>
                          <span className="inline-block bg-mf-black text-white px-5 py-2 rounded-lg text-sm font-bold" data-id="element-469">
                            {text.photosButton}
                          </span>
                          <input type="file" name="photos" multiple accept="image/*,video/*" onChange={e => addFiles(e.target.files)} className="hidden" data-id="element-470" />
                        </div>
                      </label>

                      {/* Previews */}
                      {files.length > 0 && <div className="mt-5" data-id="element-471">
                          <div className="flex items-center justify-between mb-3 text-sm" data-id="element-472">
                            <span className="font-bold text-mf-black" data-id="element-473">
                              {files.length} {text.photosSelected} · {totalMB}{' '}
                              MB
                            </span>
                            <span className="text-xs text-gray-500" data-id="element-474">
                              {MAX_FILES} {text.maxFilesNote} ·{' '}
                              {text.maxTotalNote}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-id="element-475">
                            {files.map((f, i) => <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 group" data-id="element-476">
                                {f.isVideo ? <div className="w-full h-full bg-mf-black flex flex-col items-center justify-center text-white p-2" data-id="element-477">
                                    <VideoIcon size={28} className="mb-1" data-id="element-478" />
                                    <span className="text-[10px] truncate max-w-full" data-id="element-479">
                                      {f.file.name}
                                    </span>
                                  </div> : <img src={f.previewUrl} alt={f.file.name} className="w-full h-full object-cover" data-id="element-480" />}
                                <button type="button" onClick={() => removeFile(i)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 hover:bg-mf-red text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove" data-id="element-481">
                                  <Trash2 size={14} data-id="element-482" />
                                </button>
                              </div>)}
                          </div>
                          {filesTooLargeToAttach && <div className="mt-4 bg-mf-yellow/10 border border-mf-yellow/40 rounded-xl p-4 flex items-start text-sm" data-id="element-483">
                              <MessageCircle size={18} className="text-mf-black mr-3 flex-shrink-0 mt-0.5" data-id="element-484" />
                              <p className="text-mf-darkgray font-medium" data-id="element-485">
                                {text.largeFilesNote}
                              </p>
                            </div>}
                        </div>}
                    </Section>
                  </form>
                </div>

                {/* Sticky footer */}
                <div className="border-t border-gray-200 bg-white px-6 md:px-10 py-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end flex-shrink-0" data-id="element-486">
                  <button type="button" onClick={onClose} disabled={formState === 'sending'} className="px-6 py-3 rounded-xl font-bold text-mf-darkgray hover:bg-gray-100 transition-colors disabled:opacity-40" data-id="element-487">
                    {text.cancel}
                  </button>
                  <motion.button type="button" onClick={() => formRef.current?.requestSubmit()} disabled={formState === 'sending'} whileHover={{
              scale: formState === 'sending' ? 1 : 1.02
            }} whileTap={{
              scale: formState === 'sending' ? 1 : 0.98
            }} className="flex items-center justify-center px-8 py-4 rounded-xl bg-mf-red hover:bg-red-700 text-white font-condensed font-black text-lg uppercase tracking-wider shadow-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors" data-id="element-488">
                    {formState === 'sending' ? <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24" data-id="element-489">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-490" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-491" />
                        </svg>
                        {text.submitting}
                      </> : text.submit}
                  </motion.button>
                </div>
              </>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// ---------- Subcomponents ----------
function Section({
  icon,
  title,
  children
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-492">
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200" data-id="element-493">
        <div className="w-9 h-9 rounded-lg bg-mf-red/10 text-mf-red flex items-center justify-center" data-id="element-494">
          {icon}
        </div>
        <h3 className="text-lg font-condensed font-black uppercase tracking-wider text-mf-black" data-id="element-495">
          {title}
        </h3>
      </div>
      {children}
    </div>;
}
function Field({
  label,
  fullWidth,
  children
}: {
  label: string;
  fullWidth?: boolean;
  children: React.ReactNode;
}) {
  return <div className={fullWidth ? 'md:col-span-2' : ''} data-id="element-496">
      <label className="block text-sm font-bold text-mf-darkgray mb-2 uppercase tracking-wide" data-id="element-497">
        {label}
      </label>
      {children}
    </div>;
}
function Input({
  name,
  type = 'text',
  required,
  placeholder,
  icon
}: {
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  icon?: React.ReactNode;
}) {
  return <div className="relative" data-id="element-498">
      {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400" data-id="element-499">
          {icon}
        </div>}
      <input type={type} name={name} required={required} placeholder={placeholder} className={`block w-full ${icon ? 'pl-10' : 'pl-3'} pr-3 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-mf-red bg-gray-50 transition-colors`} data-id="element-500" />
    </div>;
}
function Textarea({
  name,
  required,
  rows,
  placeholder
}: {
  name: string;
  required?: boolean;
  rows?: number;
  placeholder?: string;
}) {
  return <div className="relative" data-id="element-501">
      <div className="absolute top-3 left-3 pointer-events-none text-gray-400" data-id="element-502">
        <FileText size={16} data-id="element-503" />
      </div>
      <textarea name={name} rows={rows} required={required} placeholder={placeholder} className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-mf-red bg-gray-50 transition-colors resize-none" data-id="element-504" />
    </div>;
}
function Select({
  name,
  required,
  options
}: {
  name: string;
  required?: boolean;
  options: {
    value: string;
    label: string;
  }[];
}) {
  return <div className="relative" data-id="element-505">
      <select name={name} required={required} defaultValue="" className="block w-full pl-4 pr-10 py-3 text-base border-2 border-gray-200 focus:outline-none focus:border-mf-red rounded-xl appearance-none bg-gray-50 font-medium transition-colors cursor-pointer" data-id="element-506">
        <option value="" disabled data-id="element-507">
          --
        </option>
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-508">
            {opt.label}
          </option>)}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-509">
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-510">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-511" />
        </svg>
      </div>
    </div>;
}
function SuccessView({
  text,
  onClose,
  hadLargeFiles
}: {
  text: any;
  onClose: () => void;
  hadLargeFiles: boolean;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    type: 'spring',
    stiffness: 100,
    damping: 15
  }} className="overflow-y-auto p-8 md:p-12 text-center" data-id="element-512">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 200,
      damping: 15,
      delay: 0.15
    }} className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-green-100 text-green-600 mb-6" data-id="element-513">
        <CheckCircle2 size={56} strokeWidth={2} data-id="element-514" />
      </motion.div>

      <h3 className="text-3xl md:text-5xl font-condensed font-black text-mf-black uppercase tracking-tight mb-6" data-id="element-515">
        {text.success.title}
      </h3>

      <p className="text-base md:text-lg text-gray-700 max-w-xl mx-auto leading-relaxed mb-2" data-id="element-516">
        {text.success.message}
      </p>

      <a href={`tel:+1${text.success.phone.replace(/[^\d]/g, '')}`} className="inline-flex items-center justify-center my-4 px-6 py-3 rounded-xl bg-mf-red text-white font-condensed font-black text-2xl tracking-wider hover:bg-red-700 transition-colors shadow-lg" data-id="element-517">
        <PhoneCall className="mr-3" size={28} data-id="element-518" />
        {text.success.phone}
      </a>

      <p className="text-base md:text-lg text-gray-700 max-w-xl mx-auto leading-relaxed mt-2" data-id="element-519">
        {text.success.message2}
      </p>

      {/* Next steps card */}
      <div className="mt-8 max-w-xl mx-auto bg-gray-50 border border-gray-200 rounded-2xl p-5 text-left" data-id="element-520">
        <h4 className="font-condensed font-black uppercase tracking-wider text-mf-black text-sm mb-3 flex items-center" data-id="element-521">
          <CheckCircle2 size={16} className="text-mf-red mr-2" data-id="element-522" />
          {text.success.nextStepsTitle}
        </h4>
        <ol className="space-y-2 text-sm text-gray-700" data-id="element-523">
          {text.success.nextSteps.map((step: string, i: number) => <li key={i} className="flex items-start" data-id="element-524">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-mf-red text-white text-[10px] font-black flex items-center justify-center mr-2 mt-0.5" data-id="element-525">
                {i + 1}
              </span>
              <span data-id="element-526">{step}</span>
            </li>)}
        </ol>
      </div>

      {/* Large files reminder */}
      {hadLargeFiles && <div className="mt-5 max-w-xl mx-auto bg-mf-yellow/10 border border-mf-yellow/40 rounded-2xl p-4 text-left flex items-start" data-id="element-527">
          <MessageCircle size={18} className="text-mf-black mr-3 flex-shrink-0 mt-0.5" data-id="element-528" />
          <div data-id="element-529">
            <p className="text-sm font-bold text-mf-black mb-1" data-id="element-530">
              {text.success.largeFilesTitle}
            </p>
            <p className="text-sm text-mf-darkgray" data-id="element-531">
              {text.success.largeFilesMessage}
            </p>
          </div>
        </div>}

      <button onClick={onClose} className="mt-10 px-8 py-3 rounded-xl bg-mf-black text-white font-condensed font-black uppercase tracking-wider hover:bg-gray-800 transition-colors" data-id="element-532">
        {text.success.cta}
      </button>
    </motion.div>;
}